import { ComponentPropsWithoutRef } from 'react';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { AssignTaskAction } from 'components/tasks/AssignTaskAction';
import { CompleteTaskAction } from 'components/tasks/CompleteTaskAction';
import { DueAtAction } from 'components/tasks/DueAtAction';
import { StarTaskAction } from 'components/tasks/StarTaskAction';
import { LinkedResourceLinks } from 'components/tasks/list/LinkedResourceLinks';
import { DraggableItem } from 'components/tasks/list/TaskGroup';
import { useTaskDrawerState } from 'components/tasks/useTaskDrawerState';
import { dateFromServer } from 'helpers/dates';
import { twMerge } from 'tailwind-merge';
import { Task } from 'types/domain';

interface TaskCardsProps extends ComponentPropsWithoutRef<'div'> {}
const TaskCards = ({ children, className, ...restProps }: TaskCardsProps) => {
    return (
        <div
            className={twMerge('flex w-full flex-col items-start gap-4', className)}
            {...restProps}
        >
            {children}
        </div>
    );
};

interface TaskCardProps extends ComponentPropsWithoutRef<'div'> {
    task: Task;
    onNavigate: () => void;
}

const TaskCard = ({ task, onNavigate, children, className, ...restProps }: TaskCardProps) => {
    const { title, completed_at, due_at, status } = task;
    const { setIsDrawerOpen } = useTaskDrawerState();
    const isComplete = status === 'complete' && !!completed_at;

    return (
        <DraggableItem itemId={task.id} key={task.id}>
            <Box
                className={twMerge('group relative overflow-visible p-4', className)}
                {...restProps}
            >
                {children}
                <Button
                    variant="transparent"
                    className="z-layerThree absolute inset-0 h-full w-full"
                    onClick={onNavigate}
                    aria-label={`Navigate to Task: ${task.title}`}
                />
                <div className="flex gap-4">
                    <CompleteTaskAction task={task} />
                    <div className="my-auto flex w-full flex-col gap-2">
                        <div className="flex max-w-max flex-col items-start gap-0.5">
                            <p className="text-sm font-medium text-gray-900">{title}</p>
                            <LinkedResourceLinks
                                onClickLink={() => setIsDrawerOpen(false)}
                                links={task.links}
                            />
                        </div>
                        {isComplete || due_at ? (
                            <div className="flex items-start justify-between">
                                {isComplete ? (
                                    <p className="text-sm text-green-500">
                                        Completed {dateFromServer(completed_at).toFormat('LLL d')}
                                    </p>
                                ) : (
                                    <DueAtAction task={task} />
                                )}
                            </div>
                        ) : null}
                    </div>
                    <div className="flex flex-col justify-end">
                        <div className="flex items-center gap-2.5">
                            <StarTaskAction task={task} />
                            <AssignTaskAction task={task} />
                        </div>
                    </div>
                </div>
            </Box>
        </DraggableItem>
    );
};

interface TaskDraggingCardProps extends ComponentPropsWithoutRef<'div'> {
    task: Task;
}
const TaskDraggingCard = ({ task, className, ...restProps }: TaskDraggingCardProps) => {
    const { title } = task;
    return (
        <Box
            className={twMerge(
                'rounded-lg p-2 shadow-[0_0_0_8px_rgba(209,209,214,0.5)]',
                className,
            )}
            {...restProps}
        >
            <p className="text-base font-medium text-gray-900">{title}</p>
        </Box>
    );
};

const Root = TaskCards;
const Card = TaskCard;
const DraggingCard = TaskDraggingCard;

export {
    TaskCards,
    TaskCard,
    TaskDraggingCard,
    //
    Root,
    Card,
    DraggingCard,
};
